import {
  Heading,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { ProfileImage } from '@/components/CompanyHit/components/ProfileImage';
import { GlobeIcon, MapPinIcon, PhoneIcon } from '@/imports/ui/chakra/feather';

import { MemberCompanyDirectory_Search_memberCompanyDirectorySearch_results } from '../../hooks/graphql-types/MemberCompanyDirectory_Search';

type Address = NonNullable<
  MemberCompanyDirectory_Search_memberCompanyDirectorySearch_results['company']
>['address'];

const formatAddress = (address: Address | null): string | null => {
  if (!address) return null;
  const parts = [address.city, address.state, address.country].filter(Boolean);
  if (parts.length === 0 && address.streetAddress) return address.streetAddress;
  if (parts.length === 0) return null;
  return parts.join(', ');
};

export const MemberCompanyDirectoryHit: React.FC<{
  company: MemberCompanyDirectory_Search_memberCompanyDirectorySearch_results;
}> = ({ company }) => {
  const addressLine = formatAddress(company.company?.address ?? null);

  return (
    <LinkBox>
      <Stack direction="row" spacing="4">
        <ProfileImage
          src={company.profilePictureUrl || undefined}
          alt={company.displayName}
          preview
        />
        <Stack flex="1" spacing="2">
          <LinkOverlay as={NextLink} href={`/companies/${company._id}`}>
            <Heading size="sm">{company.displayName}</Heading>
          </LinkOverlay>

          {company.aboutUsPlainText && (
            <Text color="gray.600" mt="4" mb="2">
              {company.aboutUsPlainText}
            </Text>
          )}

          <Wrap color="gray.600" mt="2">
            {company.company?.website && (
              <WrapItem>
                <Text wordBreak="break-word">
                  <GlobeIcon mr="1" boxSize={3} color="primary.500" />
                  {company.company.website}
                </Text>
              </WrapItem>
            )}
            {company.company?.phoneNumber && (
              <WrapItem>
                <Text>
                  <PhoneIcon mr="1" boxSize={3} color="primary.500" />
                  {company.company.phoneNumber}
                </Text>
              </WrapItem>
            )}
            {addressLine && (
              <WrapItem>
                <Text>
                  <MapPinIcon mr="1" boxSize={3} color="primary.500" />
                  {addressLine}
                </Text>
              </WrapItem>
            )}
            {company.cardAttributes.map((attribute) => (
              <WrapItem key={attribute.attributeSchemaId}>
                <Text color="gray.600">
                  {attribute.name}: {attribute.displayValues.join(', ')}
                </Text>
              </WrapItem>
            ))}
          </Wrap>
        </Stack>
      </Stack>
    </LinkBox>
  );
};
